import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { ArrowBackIos, ArrowForwardIos } from '@lichtblick/icons';

import { Container, Item, MobileArrowButton, NextArrowWrapper, PrevArrowWrapper } from './Carousel.styles';

type ArrowType = {
  className: string | undefined;
  onClick: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  style: React.CSSProperties | undefined;
};

const NextArrow = ({ className, onClick, style }: ArrowType) => (
  <NextArrowWrapper className={className} style={{ ...style, zIndex: 10000 }}>
    <MobileArrowButton
      icon={<ArrowForwardIos />}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    />
  </NextArrowWrapper>
);

const PrevArrow = ({ className, onClick, style }: ArrowType) => (
  <PrevArrowWrapper className={className} style={{ ...style, zIndex: 10000 }}>
    <MobileArrowButton
      icon={<ArrowBackIos />}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    />
  </PrevArrowWrapper>
);

type SliderContent = {
  content: React.ReactNode;
  id: number;
};

export const Carousel = forwardRef<
  { goToSlide: (idx: number) => void },
  { onSlideChange?: (currentIndex: number) => void; sliders: SliderContent[] }
>(({ onSlideChange, sliders }, ref) => {
  const sliderRef = useRef<Slider | null>(null);

  useImperativeHandle(ref, () => ({
    goToSlide: (idx: number) => {
      sliderRef.current?.slickGoTo(idx);
    },
  }));

  const defaultArrowProps = {
    className: '',
    onClick: () => {},
    style: {},
    currentSlide: 0,
    slideCount: 0,
  };

  const isSingleSlide = sliders.length === 1;

  const settings = {
    infinite: !isSingleSlide,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isSingleSlide,
    nextArrow: <NextArrow {...defaultArrowProps} />,
    prevArrow: <PrevArrow {...defaultArrowProps} />,
    beforeChange: (_: number, next: number) => {
      onSlideChange?.(next);
    },
  };

  return (
    <Container>
      <Slider ref={sliderRef} {...settings}>
        {sliders.map((slider) => (
          <Item key={slider.id}>{slider.content}</Item>
        ))}
      </Slider>
    </Container>
  );
});
